<template>
    <div class="builds-page" v-reveal="{ anim: 'unfold', stagger: 90, y: 28 }">
        <!-- 顶部统计 -->
        <section class="stats-grid" v-reveal="{ anim: 'pop', stagger: 80, y: 22 }">
            <StatCard label="版本总数" :value="rows.length" icon="🧬" accent="violet" hint="已登记的构建" />
            <StatCard label="已启用" :value="enabledCount" icon="✓" accent="mint" hint="客户端可用版本" />
            <StatCard label="强制更新" :value="forceCount" icon="⬆" accent="amber" hint="开启强更的版本" />
            <StatCard label="显示中" :value="filtered.length" icon="🔎" accent="cyan" hint="符合搜索条件" />
        </section>

        <!-- 工具栏 -->
        <section class="toolbar card neon">
            <div class="search-box">
                <span class="search-ico">🔍</span>
                <input class="input search" v-model.trim="q" placeholder="搜索版本号 / 构建号 / 说明…" />
            </div>
            <div class="tb-right">
                <span class="badge">显示 <b>{{ filtered.length }}</b> / {{ rows.length }}</span>
                <div class="view-switch">
                    <button :class="{ on: view === 'grid' }" @click="view = 'grid'" title="卡片视图">▦</button>
                    <button :class="{ on: view === 'table' }" @click="view = 'table'" title="表格视图">≣</button>
                </div>
                <button class="btn" @click="load()">刷新</button>
                <button class="btn btn-primary" @click="formOpen = !formOpen">
                    {{ formOpen ? '收起新增' : '新增版本' }}
                </button>
            </div>
        </section>

        <!-- 新增版本表单 -->
        <transition name="collapse">
            <section v-if="formOpen" class="form-card card neon glow">
                <div class="form-grid">
                    <div class="field">
                        <div class="cap">版本号</div>
                        <input class="input" v-model.trim="form.version" placeholder="例如 1.0.0" />
                    </div>
                    <div class="field">
                        <div class="cap">构建号</div>
                        <input class="input" v-model.trim="form.build_id" placeholder="例如 2026-04-21-build-001" />
                    </div>
                    <div class="field">
                        <div class="cap">Core Hash（可空）</div>
                        <input class="input" v-model.trim="form.core_hash" placeholder="可空" />
                    </div>
                    <div class="field full">
                        <div class="cap">Loader Hash</div>
                        <textarea class="input textarea mono" v-model.trim="form.loader_hash"
                            placeholder="粘贴真实 loader_hash"></textarea>
                    </div>
                    <div class="field full">
                        <div class="cap">说明</div>
                        <textarea class="input textarea" v-model.trim="form.release_note"
                            placeholder="例如：当前正式版本"></textarea>
                    </div>
                    <div class="toggles full">
                        <label class="toggle">
                            <input type="checkbox" v-model="form.is_enabled" />
                            <span>新增后立即启用</span>
                        </label>
                        <label class="toggle">
                            <input type="checkbox" v-model="form.force_update" />
                            <span>新增后立即强制更新</span>
                        </label>
                    </div>
                    <div class="form-actions full">
                        <button class="btn btn-primary" @click="addBuild">新增版本</button>
                        <button class="btn" @click="fillExample">填入示例</button>
                    </div>
                </div>
            </section>
        </transition>

        <!-- 卡片视图 -->
        <div v-if="view === 'grid'" class="cards">
            <TiltCard v-for="(r, i) in filtered" :key="r.build_id" :max="6" :disabled="isTouch" class="build-card">
                <div class="card-in" :style="{ animationDelay: Math.min(i * 0.045, 0.7) + 's' }">
                    <div class="bc-top">
                        <div class="bc-version">
                            <span class="ver">v{{ r.version }}</span>
                            <span class="muted2 created">{{ fmtTime(r.created_at) }}</span>
                        </div>
                        <div class="bc-tags">
                            <span class="pill" :class="Number(r.is_enabled) === 1 ? 'pill-good' : 'pill-bad'">
                                {{ Number(r.is_enabled) === 1 ? '已启用' : '已停用' }}
                            </span>
                            <span class="pill" :class="Number(r.force_update) === 1 ? 'pill-warn' : 'pill-ok'">
                                {{ Number(r.force_update) === 1 ? '强更' : '正常' }}
                            </span>
                        </div>
                    </div>

                    <div class="bc-row">
                        <span class="muted2">构建号</span>
                        <span class="mono ellipsis" :title="r.build_id">{{ r.build_id }}</span>
                    </div>
                    <div class="bc-row">
                        <span class="muted2">Loader</span>
                        <span class="mono ellipsis" :title="r.loader_hash">{{ r.loader_hash }}</span>
                    </div>
                    <div class="bc-note muted" v-if="r.release_note">📝 {{ r.release_note }}</div>

                    <hr class="sep" />

                    <div class="bc-actions">
                        <div class="act-line">
                            <button class="btn btn-mini" @click="enableBuild(r.build_id)">启用</button>
                            <button class="btn btn-mini btn-danger" @click="disableBuild(r.build_id)">停用</button>
                        </div>
                        <div class="act-line">
                            <button class="btn btn-mini" @click="setForceUpdate(r, 1)">开强更</button>
                            <button class="btn btn-mini" @click="setForceUpdate(r, 0)">关强更</button>
                            <button class="btn btn-mini btn-danger" @click="openDelete(r.build_id)">删除</button>
                        </div>
                    </div>
                </div>
            </TiltCard>
        </div>

        <!-- 表格视图 -->
        <div v-else class="table-card card neon">
            <div class="table-scroll">
                <table class="table">
                    <thead>
                        <tr>
                            <th>版本号</th>
                            <th>构建号</th>
                            <th>启用</th>
                            <th>强更</th>
                            <th>Loader Hash</th>
                            <th>说明</th>
                            <th class="ta-right">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="r in filtered" :key="r.build_id" class="row-hover">
                            <td>
                                <div>v{{ r.version }}</div>
                                <div class="small muted2">{{ fmtTime(r.created_at) }}</div>
                            </td>
                            <td class="mono"><div class="ellipsis" :title="r.build_id">{{ r.build_id }}</div></td>
                            <td><span class="pill" :class="Number(r.is_enabled) === 1 ? 'pill-good' : 'pill-bad'">{{ Number(r.is_enabled) === 1 ? '已启用' : '已停用' }}</span></td>
                            <td><span class="pill" :class="Number(r.force_update) === 1 ? 'pill-warn' : 'pill-ok'">{{ Number(r.force_update) === 1 ? '强更' : '正常' }}</span></td>
                            <td class="mono"><div class="ellipsis" :title="r.loader_hash">{{ r.loader_hash }}</div></td>
                            <td><div class="note-text" :title="r.release_note || ''">{{ r.release_note || '-' }}</div></td>
                            <td>
                                <div class="t-ops">
                                    <button class="btn btn-mini" @click="enableBuild(r.build_id)">启用</button>
                                    <button class="btn btn-mini btn-danger" @click="disableBuild(r.build_id)">停用</button>
                                    <button class="btn btn-mini" @click="setForceUpdate(r, 1)">开强更</button>
                                    <button class="btn btn-mini" @click="setForceUpdate(r, 0)">关强更</button>
                                    <button class="btn btn-mini btn-danger" @click="openDelete(r.build_id)">删除</button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <EmptyState v-if="!filtered.length" title="暂无版本数据" desc="点击右上角“新增版本”登记一个客户端构建。" />

        <!-- 删除确认 -->
        <CyberModal :open="del.open" title="删除构建（不可恢复）" :message="del.build_id" ok-text="确认删除"
            @close="del.open = false" @cancel="del.open = false" @ok="doDelete">
            <div class="warn-line">
                <b class="bad">注意</b>
                <span class="muted">删除后客户端将无法校验该版本，请确认后再操作。</span>
            </div>
        </CyberModal>
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, reactive, ref } from "vue";
import { buildsApi } from "../api/builds";
import StatCard from "../components/StatCard.vue";
import TiltCard from "../components/TiltCard.vue";
import EmptyState from "../components/EmptyState.vue";
import CyberModal from "../components/CyberModal.vue";

const inst = getCurrentInstance();
function notify(type, title, message) {
    inst?.root?.proxy?.$toast?.push?.({ type, title, message });
}

const isTouch = typeof window !== "undefined" && "ontouchstart" in window;

const rows = ref([]);
const q = ref("");
const view = ref("grid");
const formOpen = ref(false);

const form = reactive({
    version: "",
    build_id: "",
    loader_hash: "",
    core_hash: "",
    release_note: "",
    is_enabled: true,
    force_update: false,
});

const del = reactive({ open: false, build_id: "" });

const filtered = computed(() => {
    const kw = String(q.value || "").trim().toLowerCase();
    if (!kw) return rows.value;
    return rows.value.filter(
        (r) =>
            String(r.version || "").toLowerCase().includes(kw) ||
            String(r.build_id || "").toLowerCase().includes(kw) ||
            String(r.release_note || "").toLowerCase().includes(kw)
    );
});

const enabledCount = computed(() => rows.value.filter((r) => Number(r.is_enabled) === 1).length);
const forceCount = computed(() => rows.value.filter((r) => Number(r.force_update) === 1).length);

function fmtTime(v) {
    if (!v) return "-";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

async function load(silent = false) {
    try {
        const out = await buildsApi.list();
        if (!out?.ok) {
            notify("bad", "版本列表加载失败", out?.code || "FAILED");
            return;
        }
        rows.value = out.rows || [];
        if (!silent) notify("ok", "已刷新", `共 ${rows.value.length} 条`);
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

async function addBuild() {
    try {
        // 布尔开关在提交时转成后端期望的 1/0
        const out = await buildsApi.add({
            version: form.version,
            build_id: form.build_id,
            loader_hash: form.loader_hash,
            core_hash: form.core_hash || null,
            is_enabled: form.is_enabled ? 1 : 0,
            force_update: form.force_update ? 1 : 0,
            release_note: form.release_note || null,
        });
        if (!out?.ok) return notify("bad", "新增失败", out?.code || "ADD_FAILED");
        notify("ok", "新增成功", form.build_id);
        // 重置表单
        form.version = "";
        form.build_id = "";
        form.loader_hash = "";
        form.core_hash = "";
        form.release_note = "";
        form.is_enabled = true;
        form.force_update = false;
        await load(true);
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

async function enableBuild(build_id) {
    try {
        const out = await buildsApi.enable({ build_id });
        if (!out?.ok) return notify("bad", "启用失败", out?.code || "ENABLE_FAILED");
        notify("ok", "已启用", build_id);
        await load(true);
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

async function disableBuild(build_id) {
    try {
        const out = await buildsApi.disable({ build_id });
        if (!out?.ok) return notify("bad", "停用失败", out?.code || "DISABLE_FAILED");
        notify("ok", "已停用", build_id);
        await load(true);
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

async function setForceUpdate(row, val) {
    try {
        // 保持原说明不变，仅切换强更开关
        const out = await buildsApi.forceUpdate({
            build_id: row.build_id,
            force_update: val,
            release_note: row.release_note || null,
        });
        if (!out?.ok) {
            return notify("bad", val ? "开启强更失败" : "关闭强更失败", out?.code || "FORCE_UPDATE_FAILED");
        }
        notify("ok", val ? "已开启强更" : "已关闭强更", row.build_id);
        await load(true);
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

function openDelete(build_id) {
    del.build_id = build_id;
    del.open = true;
}

async function doDelete() {
    const build_id = del.build_id;
    del.open = false;
    try {
        const out = await buildsApi.deleteBuild({ build_id });
        if (!out?.ok) return notify("bad", "删除失败", out?.code || "DELETE_FAILED");
        notify("ok", "已删除", build_id);
        await load(true);
    } catch (e) {
        notify("bad", "网络错误", String(e?.message || e));
    }
}

function fillExample() {
    form.version = "1.0.0";
    form.build_id = "2026-04-21-build-001";
    form.release_note = "当前正式版本";
}

onMounted(() => load(true));
</script>

<style scoped>
.builds-page {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    flex-wrap: wrap;
}

.tb-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.search-box {
    position: relative;
    flex: 1 1 240px;
    max-width: 360px;
}

.search-ico {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    opacity: .6;
    font-size: 13px;
}

.search {
    width: 100%;
    padding-left: 34px;
}

.view-switch {
    display: inline-flex;
    border: 1px solid var(--stroke);
    border-radius: 10px;
    overflow: hidden;
}

.view-switch button {
    width: 38px;
    height: 38px;
    background: transparent;
    border: none;
    color: var(--muted);
    cursor: pointer;
    font-size: 16px;
}

.view-switch button.on {
    background: var(--grad-violet-cyan);
    color: #fff;
}

/* 新增表单 */
.form-card {
    padding: 16px;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

.form-grid .full {
    grid-column: 1 / -1;
}

.textarea {
    min-height: 84px;
    resize: vertical;
}

.toggles {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 12px;
    border-radius: var(--r-sm);
    border: 1px solid var(--stroke);
    background: rgba(255, 255, 255, .04);
    cursor: pointer;
}

.form-actions {
    display: flex;
    gap: 10px;
}

/* 卡片 */
.cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
    gap: 14px;
}

.build-card {
    padding: 16px;
}

.bc-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;
}

.bc-version {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.ver {
    font-size: 18px;
    font-weight: 800;
}

.created {
    font-size: 11px;
}

.bc-tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.bc-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 12.5px;
    padding: 6px 0;
    border-top: 1px solid var(--stroke-soft);
}

.bc-note {
    margin-top: 10px;
    font-size: 12.5px;
    line-height: 1.4;
    word-break: break-word;
}

.bc-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.act-line {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.act-line .btn {
    flex: 1 1 auto;
}

.ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
}

/* 表格 */
.table-card {
    padding: 6px;
}

.table-scroll {
    overflow: auto;
    border-radius: var(--r-md);
    max-height: 70vh;
}

.ta-right {
    text-align: right;
}

.small {
    font-size: 12px;
}

.note-text {
    white-space: normal;
    word-break: break-word;
    line-height: 1.35;
    max-width: 320px;
}

.t-ops {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.warn-line {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 10px 12px;
    border-radius: var(--r-sm);
    border: 1px solid rgba(255, 176, 32, .3);
    background: rgba(255, 176, 32, .08);
}

.collapse-enter-active,
.collapse-leave-active {
    transition: opacity var(--t), transform var(--t);
}

.collapse-enter-from,
.collapse-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

@media (max-width: 1100px) {
    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .form-grid {
        grid-template-columns: 1fr 1fr;
    }
}

@media (max-width: 768px) {
    .form-grid {
        grid-template-columns: 1fr;
    }

    .cards {
        grid-template-columns: 1fr;
    }

    .search-box {
        max-width: 100%;
        flex-basis: 100%;
    }
}
</style>
